// Git Integration Service for Byte DevOps IDE
// Supports GitHub, GitLab, Bitbucket, and Azure DevOps

export interface GitProvider {
  id: string;
  name: string;
  icon: string;
  baseUrl: string;
  apiUrl: string;
  oauthUrl: string;
  clientId: string;
  clientSecret: string;
}

export interface GitRepository {
  id: string;
  name: string;
  fullName: string;
  description: string;
  url: string;
  cloneUrl: string;
  sshUrl: string;
  defaultBranch: string;
  isPrivate: boolean;
  language: string;
  stars: number;
  forks: number;
  lastUpdated: string;
  provider: string;
}

export interface GitCloneRequest {
  provider: string;
  repositoryUrl: string;
  branch?: string;
  projectName?: string;
  authToken?: string;
}

export interface GitAuthResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  scope: string;
  user: {
    id: string;
    username: string;
    email: string;
    avatar: string;
    name: string;
  };
}

export class GitIntegrationService {
  private providers: Map<string, GitProvider> = new Map();

  constructor() {
    this.initializeProviders();
  }

  private initializeProviders() {
    // GitHub
    this.providers.set('github', {
      id: 'github',
      name: 'GitHub',
      icon: 'github',
      baseUrl: 'https://github.com',
      apiUrl: 'https://api.github.com',
      oauthUrl: 'https://github.com/login/oauth/authorize',
      clientId: 'Ov23liehHZVYZMxQs7WT',
      clientSecret: '31aaed50f23d19d4f1353154134e324f6e16e12f'
    });

    // GitLab
    this.providers.set('gitlab', {
      id: 'gitlab',
      name: 'GitLab',
      icon: 'gitlab',
      baseUrl: 'https://gitlab.com',
      apiUrl: 'https://gitlab.com/api/v4',
      oauthUrl: 'https://gitlab.com/oauth/authorize',
      clientId: 'b551fe083105fe62491faaab528843493ab31d24087c9c9b6be5be24769590ec',
      clientSecret: 'gloas-a38e30417833d936ce6b8e61de6b5851a1bba32d0212d511c5b8bbf5461346c0'
    });

    // Bitbucket
    this.providers.set('bitbucket', {
      id: 'bitbucket',
      name: 'Bitbucket',
      icon: 'bitbucket',
      baseUrl: 'https://bitbucket.org',
      apiUrl: 'https://api.bitbucket.org/2.0',
      oauthUrl: 'https://bitbucket.org/site/oauth2/authorize',
      clientId: 'zE5B4vzLUR3xQfK3aq',
      clientSecret: 'B5gECUzy3CNakzxHwZ36wyVgmV9CJYTm'
    });

    // Azure DevOps
    this.providers.set('azure-devops', {
      id: 'azure-devops',
      name: 'Azure DevOps',
      icon: 'azure-devops',
      baseUrl: 'https://dev.azure.com',
      apiUrl: 'https://dev.azure.com',
      oauthUrl: 'https://app.vssps.visualstudio.com/oauth2/authorize',
      clientId: '8e3ea1ea-d4ef-4d0a-b397-40b7cabf40af',
      clientSecret: '0f5e2ed5-ee57-4bf8-9f12-f5373a8c153d'
    });
  }

  getProviders(): GitProvider[] {
    return Array.from(this.providers.values());
  }

  getProvider(id: string): GitProvider | undefined {
    return this.providers.get(id);
  }

  // OAuth Authentication
  async authenticateWithProvider(providerId: string): Promise<GitAuthResponse> {
    const provider = this.getProvider(providerId);
    if (!provider) {
      throw new Error(`Unknown provider: ${providerId}`);
    }

    // Generate state parameter for security
    const state = crypto.randomUUID();
    sessionStorage.setItem(`git_oauth_state_${providerId}`, state);

    // Build OAuth URL
    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: `${window.location.origin}/auth/callback/${providerId}`,
      scope: this.getScopeForProvider(providerId),
      state: state,
      response_type: 'code'
    });

    const authUrl = `${provider.oauthUrl}?${params.toString()}`;
    
    // Open OAuth popup
    const popup = window.open(
      authUrl,
      `git-auth-${providerId}`,
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    return new Promise((resolve, reject) => {
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          reject(new Error('Authentication cancelled'));
        }
      }, 1000);

      // Listen for auth completion
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'git-auth-success') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          popup?.close();
          resolve(event.data.authResponse);
        } else if (event.data.type === 'git-auth-error') {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          popup?.close();
          reject(new Error(event.data.error));
        }
      };

      window.addEventListener('message', messageHandler);
    });
  }

  private getScopeForProvider(providerId: string): string {
    switch (providerId) {
      case 'github':
        return 'repo,user:email';
      case 'gitlab':
        return 'read_repository,read_user';
      case 'bitbucket':
        return 'repository:read';
      case 'azure-devops':
        return 'vso.code,vso.project';
      default:
        return 'read';
    }
  }

  // Repository Operations
  async getUserRepositories(providerId: string, accessToken: string): Promise<GitRepository[]> {
    const provider = this.getProvider(providerId);
    if (!provider) {
      throw new Error(`Unknown provider: ${providerId}`);
    }

    try {
      const response = await fetch(`${provider.apiUrl}/user/repos?per_page=100&sort=updated`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch repositories: ${response.statusText}`);
      }

      const repos = await response.json();
      return repos.map((repo: any) => this.mapRepositoryData(repo, providerId));
    } catch (error) {
      console.error(`Failed to fetch repositories from ${providerId}:`, error);
      throw error;
    }
  }

  async searchRepositories(providerId: string, query: string, accessToken?: string): Promise<GitRepository[]> {
    const provider = this.getProvider(providerId);
    if (!provider) {
      throw new Error(`Unknown provider: ${providerId}`);
    }

    try {
      const headers: Record<string, string> = {
        'Accept': 'application/vnd.github.v3+json'
      };

      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${provider.apiUrl}/search/repositories?q=${encodeURIComponent(query)}&per_page=50`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to search repositories: ${response.statusText}`);
      }

      const data = await response.json();
      return data.items.map((repo: any) => this.mapRepositoryData(repo, providerId));
    } catch (error) {
      console.error(`Failed to search repositories on ${providerId}:`, error);
      throw error;
    }
  }

  private mapRepositoryData(repo: any, providerId: string): GitRepository {
    return {
      id: repo.id.toString(),
      name: repo.name,
      fullName: repo.full_name || repo.fullName,
      description: repo.description || '',
      url: repo.html_url || repo.web_url,
      cloneUrl: repo.clone_url || repo.http_url,
      sshUrl: repo.ssh_url || repo.sshUrl,
      defaultBranch: repo.default_branch || repo.defaultBranch || 'main',
      isPrivate: repo.private || repo.isPrivate || false,
      language: repo.language || '',
      stars: repo.stargazers_count || repo.stars || 0,
      forks: repo.forks_count || repo.forks || 0,
      lastUpdated: repo.updated_at || repo.lastUpdated,
      provider: providerId
    };
  }

  // Clone Repository
  async cloneRepository(request: GitCloneRequest): Promise<{ success: boolean; projectId?: string; error?: string }> {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://byte-devops-ide.square-butterfly-6b3d.workers.dev';
      
      const response = await fetch(`${apiBaseUrl}/api/projects/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to clone repository');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Failed to clone repository:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Validate Repository URL
  validateRepositoryUrl(url: string): { isValid: boolean; provider?: string; error?: string } {
    try {
      const urlObj = new URL(url);
      
      // Check for known Git providers
      if (urlObj.hostname.includes('github.com')) {
        return { isValid: true, provider: 'github' };
      } else if (urlObj.hostname.includes('gitlab.com')) {
        return { isValid: true, provider: 'gitlab' };
      } else if (urlObj.hostname.includes('bitbucket.org')) {
        return { isValid: true, provider: 'bitbucket' };
      } else if (urlObj.hostname.includes('dev.azure.com')) {
        return { isValid: true, provider: 'azure-devops' };
      } else {
        return { isValid: true }; // Generic Git URL
      }
    } catch (error) {
      return { isValid: false, error: 'Invalid URL format' };
    }
  }

  // Parse Repository URL
  parseRepositoryUrl(url: string): { owner?: string; repo?: string; provider?: string } {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(part => part);
      
      if (pathParts.length >= 2) {
        return {
          owner: pathParts[0],
          repo: pathParts[1],
          provider: this.getProviderFromHostname(urlObj.hostname)
        };
      }
      
      return {};
    } catch (error) {
      return {};
    }
  }

  private getProviderFromHostname(hostname: string): string | undefined {
    if (hostname.includes('github.com')) return 'github';
    if (hostname.includes('gitlab.com')) return 'gitlab';
    if (hostname.includes('bitbucket.org')) return 'bitbucket';
    if (hostname.includes('dev.azure.com')) return 'azure-devops';
    return undefined;
  }
}
